import React from 'react';

export const graphqlFormatterContent = {
  title: 'GraphQL Formatter',
  description: 'Format and beautify GraphQL queries with our free online tool. Clean up GraphQL code with proper indentation and syntax highlighting.',

  content: (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">What is GraphQL Formatter?</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          A GraphQL Formatter is a specialized code formatting tool that beautifies and organizes GraphQL queries, mutations, subscriptions, and schema definitions according to consistent style guidelines. GraphQL is a query language for APIs that enables clients to request exactly the data they need, making it essential for modern API development. Our formatter processes GraphQL syntax including queries, mutations, subscriptions, schema definitions, fragments, and variables, applying proper indentation, spacing, and structural organization to improve code readability and maintainability.
        </p>
        <p className="text-gray-700 leading-relaxed">
          The formatter understands GraphQL's unique syntax structure including field selection, arguments, variables, directives, and nested selections. It handles complex query structures, maintains proper alignment of brackets and braces, organizes field selections logically, and ensures consistent formatting across different GraphQL document types. The tool supports GraphQL schema definition language (SDL), query syntax, and variable definitions, making it comprehensive for all GraphQL development needs.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Why GraphQL Formatting is Important?</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          GraphQL formatting is fundamental to maintaining clean, readable API code in modern development environments. Well-formatted GraphQL queries are easier to understand, debug, and modify, reducing development time and minimizing errors. In team environments, consistent formatting ensures all developers can easily read and understand each other's queries, improving collaboration and code review efficiency. Proper formatting also helps identify structural issues and syntax errors early in the development process.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          For API documentation and client development, formatted GraphQL queries serve as clear examples and reference material. When creating API documentation, client SDKs, or educational content, well-formatted queries demonstrate best practices and make complex query structures more approachable. This clarity helps frontend developers understand API capabilities and construct efficient queries that leverage GraphQL's full potential.
        </p>
        <p className="text-gray-700 leading-relaxed">
          In debugging and optimization scenarios, properly formatted GraphQL queries make it easier to identify performance issues, analyze query complexity, and optimize data fetching patterns. Clear structure helps developers spot redundant field selections, understand query depth, and identify potential N+1 query problems. This visibility is crucial for maintaining API performance and ensuring efficient data loading in client applications.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">How to Use This GraphQL Formatter?</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Our GraphQL formatter is designed for simplicity and comprehensive formatting capabilities. Start by pasting your GraphQL code into the input area or uploading a GraphQL file from your computer. The tool automatically detects the GraphQL content type and prepares it for formatting. You can work with various GraphQL document types including queries, mutations, subscriptions, schema definitions, and fragments.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          Configure formatting options to match your team's style preferences. Choose indentation size (2, 4, or 8 spaces), bracket style (same line or new line), and field alignment preferences. The tool provides presets for common formatting styles including GraphQL best practices, Prettier-style formatting, and custom configurations. You can also enable or disable specific formatting features like sorting fields, aligning arguments, or organizing imports.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Review the formatted GraphQL code in real-time to ensure it meets your expectations. The tool shows syntax highlighting, validates GraphQL syntax, and provides error feedback for any issues found. The formatted output maintains functional equivalence while improving readability. Copy the formatted code to your clipboard, download it as a file, or export it directly to your development environment. The tool preserves comments and maintains semantic structure throughout the formatting process.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Who Should Use This GraphQL Formatter?</h3>
        <p className="text-gray-700 leading-relaxed mb-6">
          Frontend developers and React/Vue/Angular developers use our formatter for organizing GraphQL queries in client applications. When building modern web applications, managing API queries, or implementing data fetching logic, developers need clean, readable GraphQL code. The formatter helps organize complex queries, maintain consistent styling across components, and improve code maintainability in large applications.
        </p>
        <p className="text-gray-700 leading-relaxed mb-6">
          Backend developers and API architects rely on our formatter for creating and maintaining GraphQL schema definitions. When designing APIs, defining types, or implementing resolvers, developers need well-structured schema code. The formatter helps organize type definitions, maintain consistent schema structure, and create clear API documentation through properly formatted schema definitions.
        </p>
        <p className="text-gray-700 leading-relaxed mb-6">
          Full-stack developers and technical leads use our formatter for maintaining code quality across entire GraphQL implementations. When working on both client and server sides, reviewing team code, or establishing development standards, technical leads need consistent formatting tools. The formatter helps enforce coding standards, improve code review processes, and maintain professional code quality across teams.
        </p>
        <p className="text-gray-700 leading-relaxed">
          API documentation writers and technical educators use our formatter for creating clear examples and educational content. When writing API documentation, creating tutorials, or developing training materials, writers need properly formatted GraphQL examples. The formatter helps create professional-looking code examples, improve educational content quality, and make GraphQL concepts more accessible to learners.
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">GraphQL Formatting Examples and Features</h3>
        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h4 className="font-semibold text-gray-900 mb-3">Example 1: Query Formatting</h4>
          <p className="text-gray-700 mb-3">
            Formatting a complex GraphQL query with nested selections:
          </p>
          <div className="code-block">
            <code className="text-sm text-gray-800">
              Before:<br />
              query GetUser&#123;user(id:$id)&#123;name email posts&#123;title comments&#123;author&#123;name&#125;&#125;&#125;&#125;<br /><br />
              After:<br />
              query GetUser($id: ID!) &#123;<br />
              &nbsp;&nbsp;user(id: $id) &#123;<br />
              &nbsp;&nbsp;&nbsp;&nbsp;name<br />
              &nbsp;&nbsp;&nbsp;&nbsp;email<br />
              &nbsp;&nbsp;&nbsp;&nbsp;posts &#123;<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;title<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;comments &#123;<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;author &#123;<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;name<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#125;<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#125;<br />
              &nbsp;&nbsp;&nbsp;&nbsp;&#125;<br />
              &nbsp;&nbsp;&#125;<br />
              &#125;<br /><br />
              Use Case: Complex query organization
            </code>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Example 2: Schema Definition</h4>
          <p className="text-gray-700 mb-3">
            Formatting GraphQL schema type definitions:
          </p>
          <div className="code-block">
            <code className="text-sm text-gray-800">
              Before:<br />
              type User&#123;id:ID! name:String! email:String posts:[Post!]!&#125;<br /><br />
              After:<br />
              type User &#123;<br />
              &nbsp;&nbsp;id: ID!<br />
              &nbsp;&nbsp;name: String!<br />
              &nbsp;&nbsp;email: String<br />
              &nbsp;&nbsp;posts: [Post!]!<br />
              &#125;<br /><br />
              Use Case: Schema organization
            </code>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced GraphQL Formatting Features</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-purple-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Syntax Validation and Highlighting</h4>
              <p className="text-gray-700">
                Real-time syntax validation identifies GraphQL errors, missing brackets, and invalid syntax. Syntax highlighting distinguishes between keywords, field names, types, variables, and directives, making code easier to read and debug. The formatter provides immediate feedback on syntax issues and suggests corrections.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-purple-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Fragment and Variable Organization</h4>
              <p className="text-gray-700">
                Automatic organization of GraphQL fragments and variable definitions. The formatter groups related fragments, sorts variable declarations alphabetically, and maintains consistent naming conventions. This organization improves code reusability and makes complex queries more manageable.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-purple-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Custom Style Configuration</h4>
              <p className="text-gray-700">
                Flexible formatting options to match team preferences and coding standards. Configure indentation size, bracket placement, field alignment, argument formatting, and comment preservation. Create custom formatting presets for different projects or team requirements while maintaining consistency.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-purple-500 rounded-full flex-shrink-0 mt-1"></div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Schema and Query Integration</h4>
              <p className="text-gray-700">
                Seamless formatting of both GraphQL schema definitions and query documents. The tool understands the relationship between schema types and query fields, ensuring consistent formatting across API definitions and client queries. This integration maintains coherence between server and client code.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-purple-50 p-6 rounded-lg">
        <h4 className="font-semibold text-purple-900 mb-3">GraphQL Development Best Practices</h4>
        <p className="text-purple-800 leading-relaxed">
          Use consistent field ordering within queries and schema definitions. Group related fields logically and maintain alphabetical order where appropriate. Use descriptive field names and include proper comments for complex types. Keep queries focused and avoid over-fetching data. Use fragments for reusable field selections. Maintain consistent indentation and bracket style across all GraphQL documents.
        </p>
      </div>
    </div>
  ),

  faqs: [
    {
      question: 'Does formatting change GraphQL query functionality?',
      answer: 'No, formatting only changes visual appearance and structure. Your GraphQL queries will execute exactly the same but with improved readability. The formatter preserves semantic meaning while enhancing code organization and maintainability.'
    },
    {
      question: 'Can I format GraphQL schema definitions and type definitions?',
      'answer': 'Yes, our tool formats GraphQL schema definition language (SDL) including type definitions, interfaces, unions, enums, input types, and directives. It maintains proper indentation, organizes field declarations, and preserves comments and annotations.'
    },
    {
      question: 'How does the formatter handle nested GraphQL queries and fragments?',
      answer: 'The formatter automatically handles nested query structures, maintains proper indentation levels for each nesting depth, and organizes fragments logically. It preserves fragment definitions and ensures consistent formatting across complex query structures.'
    },
    {
      question: 'Can I customize the formatting style to match my team\'s standards?',
      answer: 'Yes, the formatter offers extensive customization options including indentation size, bracket placement, field alignment, argument formatting, and comment preservation. You can create custom presets and share them with your team for consistent formatting across projects.'
    },
    {
      question: 'Does the formatter validate GraphQL syntax and catch errors?',
      answer: 'Yes, the tool includes real-time syntax validation that identifies GraphQL errors, missing brackets, invalid type definitions, and syntax issues. It provides error messages and suggestions to help you fix problems before using the queries in production.'
    },
    {
      question: 'How do I format GraphQL variables and operation definitions?',
      answer: 'The formatter organizes variable definitions, operation names, and argument structures. It maintains consistent ordering of variables, proper spacing around colons and equals signs, and aligns complex argument structures for improved readability.'
    }
  ],

  relatedTools: [
    {
      name: 'JSON Formatter',
      url: '/tools/json-formatter',
      description: 'Format and validate JSON responses from GraphQL APIs'
    },
    {
      name: 'Text Editor',
      url: '/tools/text-editor',
      description: 'Edit and format GraphQL documentation and comments'
    },
    {
      name: 'JavaScript Beautifier',
      url: '/tools/js-beautifier',
      description: 'Format JavaScript code that uses GraphQL clients'
    },
    {
      name: 'Regex Tester',
      url: '/tools/regex',
      description: 'Test patterns and validate GraphQL query structures'
    },
    {
      name: 'Character Counter',
      url: '/tools/character-counter',
      description: 'Analyze GraphQL query length and complexity'
    }
  ]
} as const;
